import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { domain } from './Domain.jsx';
import useApiRequest from './useApiRequest.jsx';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import { 
    Search, Filter, Calendar, Users, MapPin, 
    Star, TrendingUp, Clock, Globe, Music, 
    Gamepad2, Briefcase, Utensils, Palette, 
    GraduationCap, Heart, Zap, Award, ArrowRight
} from 'lucide-react';

const EventCategories = () => {
    const { getRequest } = useApiRequest();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await getRequest(`${domain}/event-categories`);
                if (response.status === 200) {
                    setCategories(response.body);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
                // Fallback data if API fails
                setCategories([
                    { id: 1, name: 'Music & Concerts', description: 'Live music, concerts, and musical performances', event_count: 1250, color: 'from-purple-500 to-pink-500', icon: '🎵' },
                    { id: 2, name: 'Sports & Fitness', description: 'Sports events, fitness classes, and athletic competitions', event_count: 890, color: 'from-green-500 to-emerald-500', icon: '⚽' },
                    { id: 3, name: 'Business & Technology', description: 'Conferences, workshops, and tech meetups', event_count: 2100, color: 'from-blue-500 to-cyan-500', icon: '💼' },
                    { id: 4, name: 'Food & Drink', description: 'Food festivals, wine tastings, and culinary events', event_count: 650, color: 'from-orange-500 to-red-500', icon: '🍕' },
                    { id: 5, name: 'Arts & Culture', description: 'Art exhibitions, theater, and cultural festivals', event_count: 450, color: 'from-pink-500 to-rose-500', icon: '🎨' },
                    { id: 6, name: 'Education & Learning', description: 'Seminars, courses, and educational workshops', event_count: 1200, color: 'from-indigo-500 to-purple-500', icon: '📚' },
                    { id: 7, name: 'Health & Wellness', description: 'Wellness retreats, health seminars, and fitness events', event_count: 780, color: 'from-teal-500 to-green-500', icon: '🧘' },
                    { id: 8, name: 'Entertainment', description: 'Comedy shows, magic performances, and entertainment events', event_count: 920, color: 'from-yellow-500 to-orange-500', icon: '🎭' }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const filters = [
        { key: 'all', label: 'All Categories', icon: <Globe className="w-4 h-4" /> },
        { key: 'popular', label: 'Most Popular', icon: <TrendingUp className="w-4 h-4" /> },
        { key: 'recent', label: 'Recently Added', icon: <Clock className="w-4 h-4" /> },
        { key: 'trending', label: 'Trending', icon: <Zap className="w-4 h-4" /> }
    ];

    const filteredCategories = categories.filter(category => {
        const matchesSearch = (category.name && category.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                             (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
        
        console.log(`Category: ${category.name}, Search: "${searchTerm}", Matches: ${matchesSearch}`);
        
        if (selectedFilter === 'popular') {
            return matchesSearch && category.event_count > 1000;
        } else if (selectedFilter === 'recent') {
            return matchesSearch && category.id > 4; // Simulate recent categories
        } else if (selectedFilter === 'trending') {
            return matchesSearch && category.event_count > 800;
        }
        
        return matchesSearch;
    });

    const sortedCategories = [...filteredCategories].sort((a, b) => {
        if (selectedFilter === 'popular') return b.event_count - a.event_count;
        if (selectedFilter === 'recent') return b.id - a.id;
        if (selectedFilter === 'trending') return b.event_count - a.event_count;
        return a.name.localeCompare(b.name);
    });

    console.log('Search term:', searchTerm);
    console.log('Filtered categories:', filteredCategories.length);
    console.log('Sorted categories:', sortedCategories.length);

    if (loading) {
        return (
            <>
                <Header />
                <div className="min-h-screen bg-gray-50">
                    <div className="container mx-auto px-4 py-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                                    <div className="w-16 h-16 bg-gray-200 rounded-2xl mb-4"></div>
                                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded mb-4"></div>
                                    <div className="h-6 bg-gray-200 rounded"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    <div className="container mx-auto px-4 py-16">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="mb-8"
                            >
                                <h1 className="text-5xl font-bold mb-6">
                                    Event Categories
                                </h1>
                                <p className="text-xl text-orange-100 leading-relaxed">
                                    Discover amazing events across all categories. From music and sports to business and culture, 
                                    find your perfect event experience.
                                </p>
                            </motion.div>

                            {/* Search Bar */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2, duration: 0.6 }}
                                className="relative max-w-2xl mx-auto"
                            >
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search categories..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        console.log('Search term changed:', e.target.value);
                                        setSearchTerm(e.target.value);
                                    }}
                                    className="w-full pl-14 pr-4 py-4 bg-white text-gray-900 placeholder-gray-500 rounded-2xl text-lg focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50 shadow-lg"
                                />
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 py-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="flex flex-wrap gap-3 justify-center">
                                {filters.map((filter) => (
                                    <button
                                        key={filter.key}
                                        onClick={() => setSelectedFilter(filter.key)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                                            selectedFilter === filter.key
                                                ? 'bg-orange-500 text-white shadow-md'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {filter.icon}
                                        <span>{filter.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-12">
                    <div className="max-w-7xl mx-auto">
                        {/* Results Summary */}
                        {(searchTerm || selectedFilter !== 'all') && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center mb-8"
                            >
                                <p className="text-gray-600 text-lg">
                                    {sortedCategories.length} categor{sortedCategories.length !== 1 ? 'ies' : 'y'} found
                                    {searchTerm && ` for "${searchTerm}"`}
                                </p>
                            </motion.div>
                        )}

                        {/* Categories Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            <AnimatePresence>
                                {sortedCategories.map((category, index) => (
                                    <motion.div
                                        key={category.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ delay: index * 0.05, duration: 0.3 }}
                                        whileHover={{ y: -5 }}
                                        className="group"
                                    >
                                        <Link
                                            to={`/events?category=${(category.name || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}
                                            className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 h-full"
                                        >
                                                                                        <div className="text-center mb-4">
                                                <div className="text-4xl mb-3">{category.icon || '📋'}</div>
                                                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-300">
                                                    {category.name || 'Unnamed Category'}
                                                </h3>
                                                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                                    {category.description || 'No description available'}
                                                </p>
                                            </div>
                                            
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-gray-500">
                                                    <Calendar className="w-4 h-4" />
                                                    <span className="text-sm font-medium">
                                                        {(category.event_count || 0).toLocaleString()} events
                                                    </span>
                                                </div>
                                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-300">
                                                    <ArrowRight className="w-4 h-4 text-orange-600 group-hover:text-white transition-colors duration-300" />
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        {/* No Results */}
                        {sortedCategories.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-16"
                            >
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Search className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                                    No categories found
                                </h3>
                                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                                    Try different search terms or browse all categories to find what you're looking for.
                                </p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedFilter('all');
                                    }}
                                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-medium transition-colors duration-200"
                                >
                                    Clear Search
                                </button>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default EventCategories;
